import { Clipboard } from '@capacitor/clipboard'
import { UntitledIcon } from '@faceup/icons'
import { ulCopy01 } from '@faceup/icons/ulCopy01'
import { Prompt, UnstyledButton } from '@faceup/ui'
import { Button, Space, Typography, useMessage, useUiBaseConfig } from '@faceup/ui-base'
import { ChannelType } from '@faceup/utils'
import { lazy, Suspense as ReactSuspense, useContext, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../../../../__generated__'
import { LayoutContext } from '../../../../../Contexts/LayoutContext'
import { ReportFormContext } from '../../../../../Contexts/ReportFormContext'
import { sharedMessages } from '../../../../../Shared/translations'
import { defineMessages, FormattedMessage, useIntl } from '../../../../../TypedIntl'
import useAnalytics from '../../../../../utils/analytics'
import { copyTextForOldBrowsers } from '../../../../../utils/helpers'
import { NotifyMe } from './NotifyMe'

const PDFReportPin = lazy(() => import('./PDFReportPin'))

const { Text } = Typography

const messages = defineMessages({
  pinPrompt: 'FollowUp.CreateReportForm.pinFormPrompt',
  pinLabel: 'FollowUp.CheckReport.pinLabel',
  pinCopy: 'Shared.action.copy',
  pinCopied: 'Shared.global.copied',
  checkReport: 'FollowUp.CheckReport.title',
  beforeLeaveDescription: 'FollowUp.ReportSent.beforeLeave.description',
})

const messagesPerChannel = defineMessages<ChannelType>({
  [ChannelType.Survey]: 'FollowUp.ReportSent.pinDisclaimer.survey',
  [ChannelType.AutomatedHotline]: 'FollowUp.ReportSent.pinDisclaimer',
  [ChannelType.LiveHotline]: 'FollowUp.ReportSent.pinDisclaimer',
  [ChannelType.AiHotline]: 'FollowUp.ReportSent.pinDisclaimer',
  [ChannelType.ReportingSystem]: 'FollowUp.ReportSent.pinDisclaimer',
})

const fragments = {
  PinForm_publicReportSource: graphql(`
    fragment PinForm_publicReportSource on PublicReportSource {
      id
      shortLink
      sourceType

      ...NotifyMe_publicReportSource
    }
  `),
}

type PinFormReportingProps = {
  reportSource: FragmentType<typeof fragments.PinForm_publicReportSource>
  shortLink?: never
  sourceType?: never
}

type PinFormSurveyProps = {
  shortLink: string
  sourceType: ChannelType
  reportSource?: never
}

type PinFormProps = PinFormReportingProps | PinFormSurveyProps

function resolvePinFormProps(props: PinFormProps) {
  if ('reportSource' in props && props.reportSource) {
    const data = getFragmentData(fragments.PinForm_publicReportSource, props.reportSource)
    return { shortLink: data.shortLink, sourceType: data.sourceType, fragmentData: data }
  }
  return {
    shortLink: props.shortLink,
    sourceType: props.sourceType,
    fragmentData: null,
  }
}

export const PinForm = (props: PinFormProps) => {
  const { shortLink, sourceType, fragmentData } = resolvePinFormProps(props)
  const { formatMessage } = useIntl()
  const { victimPin, createdAt, tag } = useContext(ReportFormContext)
  const [pinPrompt, setPinPrompt] = useState(true)
  const { client } = useContext(LayoutContext)
  const message = useMessage()
  const { track } = useAnalytics()
  const { token } = useUiBaseConfig()

  const savePinToClipboard = async () => {
    track('Report pin copied')
    // if client is JOBKA, they are unable to copy PIN in webview, that means we need to call a custom function, which they inject into the page
    if (client === 'jobka') {
      if (window?.copyToClipboard) {
        // JOBKA custom function - declaration in `typings.d.ts`
        window?.copyToClipboard({ Text: victimPin ?? '' })
        message.success(formatMessage(messages.pinCopied))
        setPinPrompt(false)
        return
      }
      console.error('Function is not implemented')
    }

    try {
      await Clipboard.write({ string: victimPin ?? '' })
      message.success(formatMessage(messages.pinCopied))
      setPinPrompt(false)
    } catch {
      const copied = copyTextForOldBrowsers(victimPin ?? '')
      if (copied) {
        message.success(formatMessage(messages.pinCopied))
        setPinPrompt(false)
      } else {
        message.error(formatMessage(sharedMessages.apiError))
      }
    }
  }

  return (
    <div
      style={{
        border: `1px solid ${token.primary1}`,
        borderRadius: '0.75rem',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          backgroundColor: token.primary1,
          padding: '1.5rem',
          paddingTop: '3rem',
          paddingInline: '2rem',
        }}
      >
        <div className='flex flex-col gap-6'>
          <div>
            <Text strong style={{ textAlign: 'center', display: 'block' }}>
              <FormattedMessage {...messagesPerChannel[sourceType]} />
            </Text>
          </div>
          <UnstyledButton
            onClick={() => savePinToClipboard()}
            className='bg-white rounded-md text-center w-full'
          >
            <Text
              style={{
                fontSize: 20,
                fontWeight: 600,
                textAlign: 'center',
                display: 'block',
              }}
              className='sensitive py-2'
            >
              {victimPin}
            </Text>
          </UnstyledButton>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
            <Button
              type='primary'
              block
              data-test='report-sent-pin-copy'
              onClick={() => savePinToClipboard()}
            >
              <Space>
                <UntitledIcon icon={ulCopy01} />
                <FormattedMessage {...messages.pinCopy} />
              </Space>
            </Button>

            <ReactSuspense
              fallback={
                <Button type='primary' block loading>
                  <FormattedMessage {...sharedMessages.download} />
                </Button>
              }
            >
              <PDFReportPin
                pin={victimPin}
                createdAt={createdAt}
                shortId={shortLink}
                tag={tag ?? ''}
                onClick={() => {
                  track('Report pin downloaded')
                  setPinPrompt(false)
                }}
                channelType={sourceType}
              />
            </ReactSuspense>
          </div>
        </div>
        <Prompt when={pinPrompt} message={formatMessage(messages.pinPrompt)} />
      </div>

      {fragmentData && <NotifyMe publicReportSource={fragmentData} reportPin={victimPin ?? ''} />}
    </div>
  )
}
